import { Fragment, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import RelatorioEntregasService from "../../services/resource/RelatorioEntregasService";
import { Loading } from "../../componentes/loading/loading";
import { formatLocalDate } from "../../utils/formatDate";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const n = (v) => (v != null ? Number(v) : 0);
const fmt = (v, d = 2) => n(v).toLocaleString("pt-BR", { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtInt = (v) => n(v).toLocaleString("pt-BR");
const fmtDif = (v) => (v == null ? "—" : `${n(v) > 0 ? "+" : ""}${fmt(v)}`);
const corDif = (v, tolerancia) =>
    v == null ? "" : Math.abs(n(v)) > n(tolerancia) ? "font-bold text-rose-600" : "text-emerald-600";

const SITUACOES = {
    CONFERE: { label: "Confere", classe: "bg-emerald-600/10 text-emerald-700" },
    DIVERGENTE: { label: "Divergente", classe: "bg-rose-600/10 text-rose-700" },
    SO_SOLAR: { label: "Só na Solar", classe: "bg-amber-500/15 text-amber-700" },
    SO_SISTEMA: { label: "Só no sistema", classe: "bg-violet-600/10 text-violet-700" },
    SO_CARGA: { label: "Só na Carga", classe: "bg-blue-600/10 text-blue-700" },
};

const FILTROS = [
    { key: "DIFERENCAS", label: "Com diferença" },
    { key: "DIVERGENTE", label: "Divergentes" },
    { key: "SO_SOLAR", label: "Só na Solar" },
    { key: "SO_SISTEMA", label: "Só no sistema" },
    { key: "SO_CARGA", label: "Só na Carga" },
    { key: "CONFERE", label: "Conferem" },
    { key: "TODOS", label: "Todos" },
];

function Contagem({ titulo, valor, detalhe, cor }) {
    return (
        <div className="rounded-xl border border-border bg-card p-4 shadow-card" style={{ borderLeft: `4px solid ${cor}` }}>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{titulo}</p>
            <p className="mt-1 text-2xl font-extrabold tabular-nums text-foreground">{fmtInt(valor)}</p>
            {detalhe && <p className="mt-0.5 text-[11px] text-muted-foreground">{detalhe}</p>}
        </div>
    );
}

function SituacaoBadge({ situacao }) {
    const s = SITUACOES[situacao] ?? { label: situacao, classe: "bg-muted text-foreground" };
    return <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold ${s.classe}`}>{s.label}</span>;
}

// Soma das caixas unitárias de todos os clientes: Solar × sistema
function TabelaSomas({ r }) {
    const linhas = [
        { nome: "Volume (caixa unitária)", solar: r.somaSolarVolume, sistema: r.somaSistemaVolume },
        { nome: "Retorno (caixa unitária)", solar: r.somaSolarRetorno, sistema: r.somaSistemaRetorno },
    ];
    return (
        <div className="overflow-auto rounded-2xl border border-border bg-card shadow-card">
            <table className="w-full text-xs text-foreground">
                <thead>
                    <tr className="border-b border-border font-semibold text-muted-foreground">
                        <th className="px-4 py-2.5 text-left">Soma de todos os clientes</th>
                        <th className="px-4 py-2.5 text-right">Solar (planilha)</th>
                        <th className="px-4 py-2.5 text-right">Sistema (SAP)</th>
                        <th className="px-4 py-2.5 text-right">Diferença</th>
                    </tr>
                </thead>
                <tbody>
                    {linhas.map((l) => {
                        const dif = n(l.solar) - n(l.sistema);
                        return (
                            <tr key={l.nome} className="border-b border-border last:border-0">
                                <td className="px-4 py-2.5 font-semibold">{l.nome}</td>
                                <td className="px-4 py-2.5 text-right tabular-nums">{fmt(l.solar)}</td>
                                <td className="px-4 py-2.5 text-right tabular-nums">{fmt(l.sistema)}</td>
                                <td className={`px-4 py-2.5 text-right tabular-nums ${Math.abs(dif) > 0.5 ? "font-bold text-rose-600" : "text-emerald-600"}`}>
                                    {fmtDif(dif)}
                                </td>
                            </tr>
                        );
                    })}
                    <tr>
                        <td className="px-4 py-2.5 font-semibold">Cancelado (caixa unitária)</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{fmt(r.somaSolarCancelado)}</td>
                        <td className="px-4 py-2.5 text-right text-muted-foreground" colSpan={2}>só informado pela Solar</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

function TabelaTransportes({ r }) {
    const [filtro, setFiltro] = useState("DIFERENCAS");
    const [busca, setBusca] = useState("");
    const [aberto, setAberto] = useState(null);

    const todos = useMemo(() => r.transportes ?? [], [r.transportes]);
    const contagem = useMemo(() => {
        const c = { TODOS: todos.length, DIFERENCAS: 0 };
        todos.forEach((t) => {
            c[t.situacao] = (c[t.situacao] ?? 0) + 1;
            if (t.situacao !== "CONFERE") c.DIFERENCAS++;
        });
        return c;
    }, [todos]);

    const termo = busca.trim().toLowerCase();
    const linhas = todos.filter((t) => {
        if (filtro === "DIFERENCAS" ? t.situacao === "CONFERE" : filtro !== "TODOS" && t.situacao !== filtro) return false;
        return !termo || `${t.transporte ?? ""} ${t.filial ?? ""} ${t.carga ?? ""}`.toLowerCase().includes(termo);
    });

    return (
        <div className="rounded-2xl border border-border bg-card shadow-card">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
                <div className="flex flex-wrap gap-1.5">
                    {FILTROS.map((f) => (
                        <button
                            key={f.key}
                            type="button"
                            onClick={() => setFiltro(f.key)}
                            className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${filtro === f.key
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-border bg-card text-muted-foreground hover:bg-muted"}`}
                        >
                            {f.label} ({fmtInt(contagem[f.key] ?? 0)})
                        </button>
                    ))}
                </div>
                <input
                    type="search"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    placeholder="Buscar transporte, filial ou carga"
                    className="h-9 w-full rounded-md border border-input px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 sm:w-72"
                />
            </div>

            {!linhas.length ? (
                <p className="py-8 text-center text-xs text-muted-foreground">Nenhum transporte nesta visão.</p>
            ) : (
                <div className="max-h-[520px] overflow-auto p-3">
                    <table className="w-full whitespace-nowrap text-[11px] text-foreground">
                        <thead className="sticky top-0 bg-card">
                            <tr className="border-b border-border font-semibold text-muted-foreground">
                                <th className="px-2 py-2 text-left">Transporte</th>
                                <th className="px-2 py-2 text-left">Filial</th>
                                <th className="px-2 py-2 text-left">Data</th>
                                <th className="px-2 py-2 text-left">Situação</th>
                                <th className="px-2 py-2 text-right">Clientes</th>
                                <th className="px-2 py-2 text-right">Cx.U. Solar</th>
                                <th className="px-2 py-2 text-right">Cx.U. Sistema</th>
                                <th className="px-2 py-2 text-right">Dif.</th>
                                <th className="px-2 py-2 text-right">Retorno Solar</th>
                                <th className="px-2 py-2 text-right">Retorno Sistema</th>
                                <th className="px-2 py-2 text-right">Dif.</th>
                                <th className="px-2 py-2 text-right">Cancelado</th>
                                <th className="px-2 py-2 text-left">Carga</th>
                            </tr>
                        </thead>
                        <tbody>
                            {linhas.map((t, i) => {
                                const chave = `${t.transporte}-${i}`;
                                const expansivel = (t.detalhe ?? []).length > 0;
                                return (
                                    <Fragment key={chave}>
                                        <tr
                                            className={`border-b border-border transition-colors hover:bg-muted ${expansivel ? "cursor-pointer" : ""}`}
                                            onClick={expansivel ? () => setAberto(aberto === chave ? null : chave) : undefined}
                                            onKeyDown={expansivel ? (e) => { if (e.key === "Enter") setAberto(aberto === chave ? null : chave); } : undefined}
                                            tabIndex={expansivel ? 0 : undefined}
                                            title={expansivel ? "Clique para ver os clientes do transporte na planilha da Solar" : undefined}
                                        >
                                            <td className="px-2 py-2 font-mono font-semibold">
                                                {expansivel && <i className={`fa ${aberto === chave ? "fa-chevron-down" : "fa-chevron-right"} mr-1.5 text-[9px] text-muted-foreground`} />}
                                                {t.transporte}
                                            </td>
                                            <td className="px-2 py-2 text-muted-foreground">{t.filial ?? "—"}</td>
                                            <td className="px-2 py-2 tabular-nums">{t.data ? formatLocalDate(t.data) : "—"}</td>
                                            <td className="px-2 py-2"><SituacaoBadge situacao={t.situacao} /></td>
                                            <td className="px-2 py-2 text-right tabular-nums">{t.noSolar ? fmtInt(t.clientes) : "—"}</td>
                                            <td className="px-2 py-2 text-right tabular-nums">{t.solarVolume != null ? fmt(t.solarVolume) : "—"}</td>
                                            <td className="px-2 py-2 text-right tabular-nums">{t.sistemaVolume != null ? fmt(t.sistemaVolume) : "—"}</td>
                                            <td className={`px-2 py-2 text-right tabular-nums ${corDif(t.difVolume, r.tolerancia)}`}>{fmtDif(t.difVolume)}</td>
                                            <td className="px-2 py-2 text-right tabular-nums">{t.solarRetorno != null ? fmt(t.solarRetorno) : "—"}</td>
                                            <td className="px-2 py-2 text-right tabular-nums">{t.sistemaRetorno != null ? fmt(t.sistemaRetorno) : "—"}</td>
                                            <td className={`px-2 py-2 text-right tabular-nums ${corDif(t.difRetorno, r.tolerancia)}`}>{fmtDif(t.difRetorno)}</td>
                                            <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">{t.solarCancelado != null ? fmt(t.solarCancelado) : "—"}</td>
                                            <td className="px-2 py-2 font-mono text-muted-foreground">{t.carga ?? (t.naCarga ? "Sim" : "—")}</td>
                                        </tr>
                                        {aberto === chave && expansivel && (
                                            <tr className="border-b border-border bg-muted/40">
                                                <td colSpan={13} className="px-4 py-3">
                                                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                                        Clientes do transporte {t.transporte} na planilha da Solar
                                                    </p>
                                                    <table className="w-full max-w-3xl text-[11px]">
                                                        <thead>
                                                            <tr className="border-b border-border font-semibold text-muted-foreground">
                                                                <th className="px-2 py-1.5 text-left">Código</th>
                                                                <th className="px-2 py-1.5 text-left">Cliente</th>
                                                                <th className="px-2 py-1.5 text-right">Cx.U. volume</th>
                                                                <th className="px-2 py-1.5 text-right">Cx.U. retorno</th>
                                                                <th className="px-2 py-1.5 text-right">Cx.U. cancelado</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {t.detalhe.map((c, j) => (
                                                                <tr key={`${c.codigo}-${j}`} className="border-b border-border last:border-0">
                                                                    <td className="px-2 py-1.5 font-mono">{c.codigo || "—"}</td>
                                                                    <td className="px-2 py-1.5">{c.nome || "—"}</td>
                                                                    <td className="px-2 py-1.5 text-right tabular-nums">{fmt(c.volume)}</td>
                                                                    <td className="px-2 py-1.5 text-right tabular-nums">{fmt(c.retorno)}</td>
                                                                    <td className="px-2 py-1.5 text-right tabular-nums">{fmt(c.cancelado)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

/**
 * Importa a planilha de fechamento da Solar e confere, pelo filtro selecionado na tela, com o volume/retorno e a
 * Carga de Entrega. A planilha não é gravada: é lida só nas colunas A, C, D, G, P, Y e AA e descartada.
 *
 * @param parametros função que devolve o filtro atual da tela (filiais, período, modo e campo de data)
 */
export default function ConferenciaFechamentoSolar({ parametros, descricaoFiltro }) {
    const relatorioService = useMemo(() => new RelatorioEntregasService(), []);
    const inputRef = useRef(null);
    const [arquivo, setArquivo] = useState(null);
    const [resultado, setResultado] = useState(null);
    const [filtroUsado, setFiltroUsado] = useState(null);
    const [conferindo, setConferindo] = useState(false);

    const conferir = async (file) => {
        setConferindo(true);
        try {
            const res = await relatorioService.conferenciaSolar(parametros(), file);
            setResultado(res.data);
            setFiltroUsado(descricaoFiltro);
        } catch (e) {
            // o interceptor da API já mostra a mensagem do servidor
            setResultado(null);
            if (!e?.response) toast.error("Não foi possível conferir a planilha.");
        } finally {
            setConferindo(false);
        }
    };

    const aoEscolherArquivo = (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // permite escolher o mesmo arquivo de novo
        if (!file) return;
        if (!/\.(xlsx|xlsm|xls)$/i.test(file.name)) {
            toast.error("Envie a planilha de fechamento da Solar em Excel (.xlsx).");
            return;
        }
        setArquivo(file);
        conferir(file);
    };

    const limpar = () => {
        setArquivo(null);
        setResultado(null);
        setFiltroUsado(null);
    };

    // a planilha da Solar não traz a filial e costuma vir de uma só: a conferência é sempre de uma filial
    const filiaisDoFiltro = parametros().filiais ?? [];
    const umaFilial = filiaisDoFiltro.length === 1;

    const r = resultado;
    const filtroMudou = r && filtroUsado !== descricaoFiltro;
    const foraDoPeriodo = r && r.arquivoDataInicial && (r.arquivoDataInicial < r.periodoInicial || r.arquivoDataFinal > r.periodoFinal);

    return (
        <section id="conferencia-solar" className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                    <h3 className="flex items-center gap-2 text-sm font-bold text-foreground">
                        <span className="h-4 w-[3px] rounded-sm bg-amber-500" />
                        Conferência do fechamento da Solar
                    </h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                        Importe a planilha de fechamento enviada pela Solar para conferir com o volume/retorno e a Carga de Entrega
                        ({descricaoFiltro}). A planilha vem de uma só filial: a pesquisa é somente na filial selecionada no filtro. São lidas só as colunas <strong className="text-foreground">A</strong> (data),{" "}
                        <strong className="text-foreground">C</strong> e <strong className="text-foreground">D</strong> (cliente),{" "}
                        <strong className="text-foreground">G</strong> (transporte), <strong className="text-foreground">P</strong> (volume),{" "}
                        <strong className="text-foreground">Y</strong> (cancelado) e <strong className="text-foreground">AA</strong> (retorno),
                        da primeira aba. A planilha não é gravada no sistema.
                    </p>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <input ref={inputRef} type="file" accept=".xlsx,.xlsm,.xls" className="hidden" onChange={aoEscolherArquivo} />
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        disabled={conferindo || !umaFilial}
                        title={umaFilial ? undefined : "Selecione uma única filial no filtro para importar a planilha"}
                        className="inline-flex h-10 items-center gap-2 rounded-xl bg-amber-500 px-4 text-sm font-semibold text-white shadow-card transition-all duration-150 hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        <i className={conferindo ? "fa fa-spinner fa-spin" : "fa fa-upload"} />
                        {conferindo ? "Conferindo..." : arquivo ? "Trocar planilha" : "Importar planilha da Solar"}
                    </button>
                    {arquivo && !conferindo && (
                        <button
                            type="button"
                            onClick={limpar}
                            className="inline-flex h-10 items-center gap-2 rounded-xl border border-border bg-card px-3 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted"
                        >
                            <i className="fa fa-times" /> Limpar
                        </button>
                    )}
                </div>
            </div>

            {!umaFilial && (
                <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-xs text-foreground">
                    <i className="fa fa-info-circle mr-1.5 text-amber-600" />
                    Selecione <strong>uma única filial</strong> no filtro no topo (hoje: {filiaisDoFiltro.length ? `${filiaisDoFiltro.length} filiais` : "todas as filiais"})
                    para importar a planilha da Solar.
                </div>
            )}

            {conferindo && <Loading />}

            {r && !conferindo && (
                <>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-border bg-card px-4 py-3 text-xs text-muted-foreground">
                        <span><i className="fa fa-file-excel-o mr-1.5 text-emerald-600" /><strong className="text-foreground">{r.arquivo}</strong></span>
                        <span>{fmtInt(r.linhasLidas)} linhas lidas{r.linhasIgnoradas > 0 && ` · ${fmtInt(r.linhasIgnoradas)} ignoradas (cabeçalho, totais ou sem transporte)`}</span>
                        {r.arquivoDataInicial && (
                            <span>Datas da planilha: {formatLocalDate(r.arquivoDataInicial)} a {formatLocalDate(r.arquivoDataFinal)}</span>
                        )}
                        <span>Período conferido no sistema: {formatLocalDate(r.periodoInicial)} a {formatLocalDate(r.periodoFinal)}</span>
                        <span>Tolerância: {fmt(r.tolerancia)} cx. por transporte</span>
                    </div>

                    {filtroMudou && (
                        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-xs text-foreground">
                            <span>
                                <i className="fa fa-exclamation-triangle mr-1.5 text-amber-600" />
                                O filtro da tela mudou depois da conferência ({filtroUsado}). Confira novamente para usar o filtro atual.
                            </span>
                            <button
                                type="button"
                                onClick={() => conferir(arquivo)}
                                disabled={!umaFilial}
                                className="rounded-lg bg-amber-500 px-3 py-1.5 font-semibold text-white hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                Conferir novamente
                            </button>
                        </div>
                    )}

                    {foraDoPeriodo && (
                        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-xs text-foreground">
                            <i className="fa fa-exclamation-triangle mr-1.5 text-amber-600" />
                            As datas da planilha ({formatLocalDate(r.arquivoDataInicial)} a {formatLocalDate(r.arquivoDataFinal)}) saem do período
                            conferido no sistema ({formatLocalDate(r.periodoInicial)} a {formatLocalDate(r.periodoFinal)}). Ajuste o período e as
                            filiais do filtro no topo para o mesmo do fechamento, senão aparecerão transportes "só na Solar".
                        </div>
                    )}

                    <div className="space-y-3">
                        <h4 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Total de transportes encontrados</h4>
                        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
                            <Contagem titulo="Na planilha da Solar" valor={r.transportesSolar} detalhe={`${fmtInt(r.linhasLidas)} linhas de cliente`} cor="#ff9900" />
                            <Contagem titulo="No volume (SAP)" valor={r.transportesVolume} cor="#0d9488" />
                            <Contagem titulo="No retorno (SAP)" valor={r.transportesRetorno} cor="#dd5555" />
                            <Contagem titulo="Na Carga de Entrega" valor={r.transportesCarga} cor="#2563eb" />
                        </div>
                        <div className="grid grid-cols-2 gap-3 xl:grid-cols-5">
                            <Contagem titulo="Conferem" valor={r.confere} detalhe="Solar e volume com a mesma cx. unitária" cor="#16a34a" />
                            <Contagem titulo="Divergentes" valor={r.divergentes} detalhe="Existem nos dois, cx. unitária diferente" cor="#dd5555" />
                            <Contagem titulo="Só na Solar" valor={r.soSolar} detalhe="Não existem no volume do sistema" cor="#ff9900" />
                            <Contagem titulo="Só no sistema" valor={r.soSistema} detalhe="No volume e fora da planilha" cor="#7c3aed" />
                            <Contagem titulo="Só na Carga" valor={r.soCarga} detalhe="Nem na Solar nem no volume" cor="#2563eb" />
                        </div>
                        <div className="grid grid-cols-2 gap-3 xl:grid-cols-3">
                            <Contagem titulo="Solar com Carga" valor={r.solarEmCarga} detalhe="Transportes da Solar que existem na Carga de Entrega" cor="#16a34a" />
                            <Contagem titulo="Solar sem Carga" valor={r.solarSemCarga} detalhe="Transportes da Solar que não existem na Carga de Entrega" cor="#dd5555" />
                            <Contagem titulo="Carga sem Solar" valor={r.cargaSemSolar} detalhe="Na Carga de Entrega e fora da planilha" cor="#ff9900" />
                        </div>
                    </div>

                    <div className="space-y-3">
                        <h4 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                            Soma das caixas unitárias de todos os clientes × sistema, pelo mesmo número de transporte
                        </h4>
                        <TabelaSomas r={r} />
                    </div>

                    <div className="space-y-3">
                        <h4 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Transporte a transporte</h4>
                        <TabelaTransportes r={r} />
                    </div>
                </>
            )}
        </section>
    );
}
